import { useEffect, useState } from 'react'
import {
  PageLayout,
  SideBarNav,
  HeaderNavigation,
  Button,
  Level1NavItem,
  Level2NavItem,
  Typography,
  Spacer,
} from '@jisr-hr/ds'
import { useHistory, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'utils/hooks'
import clsx from 'classnames'
import { currentEmployeeChangeLanguage, logout } from 'redux/authUser/actionCreators'
import I18n from 'translations/i18n'
import DSIcon from 'components/DSIcon'
import { getTokenFromDB } from 'firebaseUtils'
import { unsubscribeFirebase } from 'redux/actions/firebaseActions'
import { labels } from 'utils/whitelabel'
import Announcement from 'components/global/templates/Announcement/Announcement'
import { toggleNotificationsPanel } from 'redux/appNotifications/actionCreators'
import UserSnap from 'components/UserSnap/UserSnap'
import { isEmpty } from 'lodash'
import { mixPanelAnalytics } from 'utils/mixpanel'
import useIsEmployeeOrg from 'hooks/useIsEmployeeOrg'
import { isRouteMatch } from 'utils/uiHelpers'
import OrganizationSteps from 'containers/authorised/NewOnboarding/components/OrganizationSteps'

import useListItems from './useListItems'
import ChangePasswordModal from './ChangePasswordModal'
import HelpCenter from './HelpCenter'
import useMainNavigation from './useMainNavigation'
import AccountSwitcher from './AccountSwitcher'

import TourStepThree from './components/TourStepThree'
import {
  useMappingNavigation,
  useDisabledMappingNavigation,
  useOnlyOnboardingMappingNavigation,
} from './MappingNavigation'
import OnboardingInviteAdmins from './components/OnboardingInviteAdmins'
import OnboardingHeader from './components/OnboardingHeader'
import styles from './onboardingInviteEmployees.module.css'

type NewNavigationProps = {
  children: React.ReactNode
}

const NewNavigation = ({ children }: NewNavigationProps): JSX.Element => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { pathname } = useLocation()

  const { initializeAppCalls } = useMainNavigation()

  const [onboardingInviteModalOpen, setOnboardingInviteModalOpen] = useState(false)
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false)
  const [sideBarCollapsed, setSideBarCollapsed] = useState(false)
  const [collapseDisabled, setCollapseDisabled] = useState(false)

  const { employee, step_tour, company } = useSelector(({ auth }) => auth)
  const switchableAccounts = useSelector((s) => s.switchableOrganizations.accounts)
  const onboardingSteps = useSelector((s) => s.onboardingHeaderSteps)
  const isUnread = useSelector(({ markAsRead }) => markAsRead.unread)

  const activatedModulesCount = company?.activated_modules?.length ?? 0

  const isNewOnboarding =
    employee?.organization?.new_onboarding_enabled &&
    employee?.organization.status === 'onboarding_phase'

  const isHybridPhase =
    !isNewOnboarding && employee?.organization?.new_onboarding_enabled && activatedModulesCount < 3

  const mainMapNavigation = useMappingNavigation()
  const disabledNavItems = useDisabledMappingNavigation()
  const onboardingNavItems = useOnlyOnboardingMappingNavigation()
  const handleMapNavItems = isNewOnboarding ? onboardingNavItems : mainMapNavigation
  const list = step_tour === 3 ? disabledNavItems : handleMapNavItems

  const canPostAnnouncement = employee?.permission_details.post_announcements
  const showAccountSwitcher = switchableAccounts.length > 1
  const isCurrentEmpOrg = useIsEmployeeOrg()
  const listItems = useListItems(!!isNewOnboarding)

  const toggleSidebar = (): void => {
    sessionStorage.setItem('collaps', `${!sideBarCollapsed}`)
    setSideBarCollapsed(!sideBarCollapsed)
  }

  const handleLogout = async (): Promise<void> => {
    const unsubscribe = new Promise(() =>
      getTokenFromDB({ clear: true }).then((token) => {
        if (token) return dispatch(unsubscribeFirebase({ token }))
        return null
      }),
    )
    const logoutSession = new Promise(() => dispatch(logout()))
    Promise.all([unsubscribe, logoutSession]).finally(() => history.push('/'))
  }

  const handleHeaderNavigation = (
    e: React.MouseEvent<HTMLLIElement, MouseEvent>,
    data: { value: string },
  ): void => {
    const actions = {
      ...(isCurrentEmpOrg && { view_profile: () => history.push(`/employees/${employee?.id}`) }),
      view_profile: () => history.push(`/employees/${employee?.id}`),
      change_password: () => setIsPasswordModalOpen(true),
      language: (): void => {
        dispatch(
          currentEmployeeChangeLanguage(employee?.id as number, {
            value: I18n.language === 'ar' ? 'en' : 'ar',
          }),
        )
      },
      logout: () => handleLogout(),
    }
    const actionHandler = actions[data.value]
    if (actionHandler) {
      actionHandler()
    }
  }

  const handleCollapse = (): void => {
    sessionStorage.setItem('collaps', `${false}`)
    setSideBarCollapsed(false)
    if (sideBarCollapsed && collapseDisabled) {
      setCollapseDisabled(false)
    }
  }

  useEffect(() => {
    const path = pathname.split('/')[1]
    const currentPath = Object.values(list).find((val) => val?.tab?.includes(path))
    if (isEmpty(currentPath?.menu)) {
      setCollapseDisabled(true)
      setSideBarCollapsed(true)
    } else {
      setCollapseDisabled(false)
      setSideBarCollapsed(sessionStorage.getItem('collaps') === 'true')
    }
  }, [pathname, employee?.id])

  useEffect(() => {
    initializeAppCalls()
  }, [])

  return (
    <>
      <PageLayout
        collapsed={sideBarCollapsed}
        className={clsx(onboardingSteps.title && styles.newOnboardingPageLayout)}
        sidePanel={
          <SideBarNav
            collapsed={sideBarCollapsed}
            logo={
              <div style={{ position: 'relative' }}>
                <img
                  src={labels.navLogo}
                  alt="log"
                />
                <TourStepThree />
              </div>
            }
            menuData={list}
            defaultNav={!isNewOnboarding ? 'general' : 'onboarding'}
            bottomNav={
              ['settings', (isNewOnboarding || isHybridPhase) && 'onboarding'].filter(
                Boolean,
              ) as string[]
            }
            activePath={pathname}
            onMenuClick={(nav: Level1NavItem | undefined): void => {
              if (isEmpty(nav?.menu)) {
                setSideBarCollapsed(true)
                if (nav?.path) {
                  history.push(nav.path)
                }
                if (nav?.event_name) mixPanelAnalytics({ event_name: nav.event_name })
                setCollapseDisabled(true)
              } else {
                handleCollapse()
              }
            }}
            onSubMenuClick={(item: Level2NavItem | undefined): void => {
              if (item) {
                history.push(item.path)
                if (item?.event_name) mixPanelAnalytics({ event_name: item.event_name })
              }
            }}
            {...(showAccountSwitcher && { switcherDropdown: <AccountSwitcher /> })}
          />
        }
        navigationHeader={
          <HeaderNavigation
            showToggelButton={!isNewOnboarding}
            employee={{
              name: employee?.full_name_i18n ?? '',
              avatar: employee?.avatar_thumb ?? '',
              email: employee?.email ?? '',
            }}
            showAvatarCompanyIcon={false}
            notificationButtonClick={(): void => {
              dispatch(toggleNotificationsPanel(true))
            }}
            onClickListItem={handleHeaderNavigation}
            dropdownListItems={listItems}
            toggleSidebar={toggleSidebar}
            collapsed={sideBarCollapsed}
            collapseDisabled={collapseDisabled}
            headerCheldren={
              <>
                {!(isNewOnboarding || isHybridPhase) ? (
                  <>
                    {canPostAnnouncement && <Announcement />}
                    <Button
                      size="sm"
                      variant="tertiaryGray"
                      iconLeading={<DSIcon name="bell-02" />}
                      style={{ position: 'relative' }}
                      onClick={(): void => {
                        dispatch(toggleNotificationsPanel(true))
                      }}
                    >
                      {isUnread && (
                        <span
                          style={{
                            width: 9,
                            height: 9,
                            borderRadius: '50%',
                            backgroundColor: 'var(--ds-core-color-error-500)',
                            position: 'absolute',
                            top: 4,
                            insetInlineEnd: 15,
                          }}
                        />
                      )}
                    </Button>

                    {labels.name === 'Jisr' && <HelpCenter />}
                  </>
                ) : (
                  <OnboardingHeader setOnboardingInviteModalOpen={setOnboardingInviteModalOpen} />
                )}
              </>
            }
            {...((isNewOnboarding || isHybridPhase) &&
              onboardingSteps.title && {
                className: styles.newOnboardingHeader,
                headerCheldrenStartSide: (
                  <div>
                    <Typography
                      variant="title-1"
                      text={onboardingSteps.title}
                    />
                    <Spacer height={16} />
                    <OrganizationSteps
                      list={onboardingSteps.list}
                      inProgress={pathname.split('/')[4]}
                      onClick={(v): void => history.push(`${onboardingSteps.path}/${v.key}`)}
                    />
                  </div>
                ),
              })}
          />
        }
        body={
          <>
            {children}
            <UserSnap />
          </>
        }
      />
      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        onClose={(): void => setIsPasswordModalOpen(false)}
      />
      {isRouteMatch('org_info') && (isNewOnboarding || isHybridPhase) && (
        <OnboardingInviteAdmins
          isOpen={onboardingInviteModalOpen}
          onClose={(): void => setOnboardingInviteModalOpen(false)}
        />
      )}
    </>
  )
}

export default NewNavigation
